import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { PRICE_CATEGORY } from '../setting';
import { useFirebaseToken } from '../firebase/FirebaseProvider';

const ANALYTICS_URL = 'https://flashfundme.com/analytics/events';
const ANALYTICS_API_KEY = '16cb9e127fefe94e67984212d7de10cfb64effb9ed182447d2e1c6b6c969f8e9';
const APP_VERSION = process.env.REACT_APP_COMMIT_SHA || 'dev';
const DEVICE_ID_KEY = 'analytics_device_id';
const SENT_EVENTS_KEY = 'analytics_sent_events';
const FIRST_LAUNCH_KEY = 'first_launch_tracked';

interface AnalyticsContextType {
    track: (event: string, data?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

const getOrCreateDeviceId = (): string => {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
};

const detectOS = (): { os: string; osVersion: string } => {
    const ua = navigator.userAgent;
    let match: RegExpMatchArray | null;
    if ((match = ua.match(/Windows NT ([\d.]+)/))) return { os: 'Windows', osVersion: match[1] };
    if ((match = ua.match(/iPhone OS ([\d_]+)/)) || (match = ua.match(/iPad; CPU OS ([\d_]+)/))) {
        return { os: 'iOS', osVersion: match[1].replace(/_/g, '.') };
    }
    if ((match = ua.match(/Mac OS X ([\d_.]+)/))) return { os: 'macOS', osVersion: match[1].replace(/_/g, '.') };
    if ((match = ua.match(/Android ([\d.]+)/))) return { os: 'Android', osVersion: match[1] };
    if (/Linux/.test(ua)) return { os: 'Linux', osVersion: '' };
    return { os: 'unknown', osVersion: '' };
};

const detectDevice = (): { manufacturer: string; model: string } => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return { manufacturer: 'Apple', model: 'iPhone' };
    if (/iPad/.test(ua)) return { manufacturer: 'Apple', model: 'iPad' };
    if (/Macintosh/.test(ua)) return { manufacturer: 'Apple', model: 'Mac' };
    const androidModel = ua.match(/Android[^;]*;\s*([^;)]+)\s+Build/);
    if (androidModel) return { manufacturer: 'Android', model: androidModel[1].trim() };
    if (/Windows/.test(ua)) return { manufacturer: 'Microsoft', model: 'PC' };
    return { manufacturer: '', model: '' };
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { uid, token } = useFirebaseToken();
    const sessionIdRef = useRef<string>(crypto.randomUUID());
    const deviceIdRef = useRef<string>(getOrCreateDeviceId());
    const deviceInfo = useMemo(() => ({ ...detectOS(), ...detectDevice() }), []);

    const getSentEvents = (): Set<string> => {
        try {
            const stored = localStorage.getItem(SENT_EVENTS_KEY);
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch {
            return new Set();
        }
    };

    const markEventAsSent = (event: string) => {
        const sentEvents = getSentEvents();
        sentEvents.add(event);
        localStorage.setItem(SENT_EVENTS_KEY, JSON.stringify(Array.from(sentEvents)));
    };

    const track = (event: string, data?: Record<string, any>) => {
        if (!uid || !token) return;
        if (getSentEvents().has(event)) {
            console.log('track skipped (already sent):', event);
            return;
        }

        const properties = { ...(data || {}), url_prefix: PRICE_CATEGORY };

        const payload = {
            events: [
                {
                    event_name: event,
                    user_id: uid,
                    session_id: sessionIdRef.current,
                    device_id: deviceIdRef.current,
                    client_time: new Date().toISOString(),
                    platform: 'web',
                    app_version: APP_VERSION,
                    os_version: deviceInfo.osVersion,
                    os: deviceInfo.os,
                    app_build_number: '',
                    manufacturer: deviceInfo.manufacturer,
                    model: deviceInfo.model,
                    properties,
                },
            ],
        };

        fetch(ANALYTICS_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': ANALYTICS_API_KEY,
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        }).catch((error) => {
            console.warn('Analytics error:', error);
        });

        markEventAsSent(event);
        console.log('track', event, properties);
    };

    useEffect(() => {
        if (!uid || !token) return;
        if (localStorage.getItem(FIRST_LAUNCH_KEY)) return;
        track('First Launch: View');
        localStorage.setItem(FIRST_LAUNCH_KEY, 'true');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, token]);

    return (
        <AnalyticsContext.Provider value={{ track }}>
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = (): AnalyticsContextType => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};
